import { FC, useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "../Button";
import Close from "../../../assets/icons/x-mark.svg?react";

type ModalProps = {
    isOpen: boolean;
    children?: ReactNode;
    className?: string;
    hasBackdrop?: boolean;
    showCloseButton?: boolean;
    onClose: () => void;
}

export const Modal: FC<ModalProps> = ({ isOpen, children, className = '', hasBackdrop = true, onClose, showCloseButton = true }) => {

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        if (isOpen) {
            // Calculate scrollbar width
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            // Store the original right padding to restore later
            const originalPaddingRight = document.body.style.paddingRight;

            // Hide overflow and add compensatory padding
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollBarWidth}px`;

            // Cleanup: restore original styles
            return () => {
                document.body.style.overflow = '';
                document.body.style.paddingRight = originalPaddingRight;
            };
        }
    }, [isOpen]);

    return createPortal(
        <AnimatePresence>
            {isOpen &&
                <motion.div className="fixed inset-0 z-50 flex items-center justify-center" aria-labelledby="modal-title" role="dialog" aria-modal="true" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }}>
                    <div className="absolute inset-0 bg-gray-500/75" aria-hidden="true" onClick={hasBackdrop ? onClose : undefined} />
                    <motion.div className={`relative bg-black rounded-lg overflow-clip shadow-lg z-50 ${className}`} initial={{ opacity: 0, y: 16, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.9 }} transition={{ duration: 0.3, ease: 'easeInOut' }}>
                        {children}
                        {showCloseButton && <Button variant="link" className="absolute right-4 top-4 p-1" icon={<Close className="text-white" />} onClick={onClose} />}
                    </motion.div>
                </motion.div>
            }
        </AnimatePresence>,
        document.body);
}